<template>
  <f7-card class="daily-order-item-card" role="group" :aria-labelledby="titleId">
    <div class="daily-order-item-card__main">
      <!-- Product thumbnail (NOT a buyer avatar) — generic package fallback when absent. -->
      <div class="daily-order-item-card__media">
        <img
          v-if="thumbnailUrl"
          :src="thumbnailUrl"
          :alt="itemName"
          class="daily-order-item-card__photo"
          loading="lazy"
        />
        <div v-else class="daily-order-item-card__photo daily-order-item-card__photo--placeholder">
          <f7-icon f7="cube_box" size="28"></f7-icon>
        </div>
        <span v-if="photoUrls.length > 1" class="daily-order-item-card__photo-badge">
          {{ $t('supplier.wall.posts.card.morePhotos', { count: photoUrls.length - 1 }) }}
        </span>
      </div>

      <div class="daily-order-item-card__content">
        <div class="daily-order-item-card__title-row">
          <span :id="titleId" class="daily-order-item-card__name">{{ itemName }}</span>
          <!-- «خلاص» lock tag — tappable/focusable, reveals the plain-language reason
               on demand rather than passively explaining why (design spec Section 5).
               Not drawn on a finished day: its taken / not-taken heading says it. -->
          <button
            v-if="item.frozen && !isBoxClosed"
            type="button"
            class="daily-order-item-card__lock"
            @click="onLockTap"
          >
            <f7-chip class="daily-order-item-card__lock-chip">
              <f7-icon f7="lock_fill" size="12"></f7-icon>
              {{ $t('supplier.dailyOrders.locked.tag') }}
            </f7-chip>
          </button>
        </div>

        <div v-if="variantLabel || isClaimedOrigin" class="daily-order-item-card__chips">
          <f7-chip v-if="variantLabel" class="daily-order-item-card__variant">
            {{ variantLabel }}
          </f7-chip>
          <!-- Origin badge — only the 'claimed' (pulled from another channel's
               post) case renders a chip; 'own_channel' stays the unmarked
               default, matching the card's other exception-only chips (locked
               tag, claim-taken). -->
          <f7-chip v-if="isClaimedOrigin" class="daily-order-item-card__origin">
            {{ $t('supplier.dailyOrders.item.origin.claimed') }}
          </f7-chip>
        </div>

        <div class="daily-order-item-card__amounts">
          <span class="daily-order-item-card__qty">{{ item.qty }}×</span>
          <span class="daily-order-item-card__unit">{{ formatCurrency(item.unitPrice) }}</span>
          <span class="daily-order-item-card__line-total">
            {{ $t('supplier.card.total') }} {{ formatCurrency(lineAmount) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Member's note — ALWAYS visible (deliberate deviation from the sibling
         collapsed-by-default pattern; a supplier's prep decision depends on it).
         When empty, show a quiet neutral hint rather than nothing, so the
         supplier can't mistake "no note" for a load failure. -->
    <div
      class="daily-order-item-card__note"
      :class="{ 'daily-order-item-card__note--empty': !hasMemberNote }"
    >
      <template v-if="hasMemberNote">
        <span class="daily-order-item-card__note-label">
          {{ $t('supplier.dailyOrders.item.memberNoteLabel') }}
        </span>
        <p class="daily-order-item-card__note-body">{{ item.memberNote }}</p>
      </template>
      <p v-else class="daily-order-item-card__note-empty">
        {{ $t('supplier.dailyOrders.item.noMemberNote') }}
      </p>
    </div>

    <button
      v-if="hasSourcePost"
      type="button"
      class="daily-order-item-card__view-post"
      @click="$emit('view-post', item)"
    >
      {{ $t('supplier.dailyOrders.item.viewPostAndPhotos') }}
    </button>

    <!-- Two-tap action area: hand-rolled exclusive-pair toggle (no F7 primitive
         fits a 3-state mutually-exclusive pair — design spec Section 4/Section 8). Hidden
         entirely once the whole box is closed (no answer is possible anymore);
         still shown disabled for a mid-collection per-item freeze, where the
         collection is ongoing and the disabled state is meaningful feedback. -->
    <div v-if="!isBoxClosed && !answersLocked" class="daily-order-item-card__actions">
      <f7-button
        type="button"
        class="daily-order-item-card__btn"
        :class="reserveBtnClass"
        :fill="isReserveActive"
        :outline="!isReserveActive"
        :disabled="item.frozen"
        :aria-disabled="item.frozen ? 'true' : 'false'"
        :aria-pressed="isReserveActive ? 'true' : 'false'"
        :aria-label="reserveAriaLabel"
        @click="onReserveClick"
      >
        <f7-icon f7="checkmark_alt_circle_fill" size="18"></f7-icon>
        {{ reserveLabel }}
      </f7-button>
      <f7-button
        type="button"
        class="daily-order-item-card__btn"
        :class="oosBtnClass"
        :fill="isOosActive"
        :outline="!isOosActive"
        :disabled="item.frozen"
        :aria-disabled="item.frozen ? 'true' : 'false'"
        :aria-pressed="isOosActive ? 'true' : 'false'"
        :aria-label="oosAriaLabel"
        @click="onOosClick"
      >
        <f7-icon f7="xmark_circle_fill" size="18"></f7-icon>
        {{ $t('supplier.dailyOrders.actions.oos') }}
      </f7-button>
    </div>
  </f7-card>
</template>

<script setup>
import { computed, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency.js';
import { useToast } from '@/composables/useToast.js';
import {
  frozenReasonI18nKey,
  isOosSignal,
  isReservedSignal,
  SupplierFrozenReason,
} from '@/enums/commerce/SupplierSignal.enum.js';

const props = defineProps({
  // A SupplierDailyOrderItem ORM model (or a plain object with the same shape).
  item: {
    type: Object,
    required: true,
  },
  // Showroom stage: the day is read-only, so the reserve / out-of-stock pair
  // is not drawn (the page carries the one lock sentence).
  answersLocked: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['reserve', 'oos', 'clear', 'view-post']);

const { t } = useI18n();
const { formatCurrency } = useCurrency();
const { showToast } = useToast();

const titleId = `daily-order-item-name-${useId()}`;

const itemName = computed(() => props.item.productName || '');
const variantLabel = computed(() => props.item.variantLabel || '');

// Defensive fallback for plain fixture/mock objects that don't carry the
// model's own `photoUrls` getter (the single normalizer for the gallery vs
// single-photo shape — see SupplierDailyOrderItem.js). A real model instance's
// getter always returns an array and short-circuits on the first line; this
// mirrors that same algorithm only when the getter itself isn't present.
const photoUrls = computed(() => {
  if (Array.isArray(props.item.photoUrls)) return props.item.photoUrls;
  const gallery = (Array.isArray(props.item.catalogPhotos) ? props.item.catalogPhotos : [])
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') return entry.url ?? entry.src ?? entry.href ?? null;
      return null;
    })
    .filter(Boolean);
  if (gallery.length) return gallery;
  return props.item.photoUrl ? [props.item.photoUrl] : [];
});
const thumbnailUrl = computed(() => photoUrls.value[0] || null);

const hasMemberNote = computed(
  () => typeof props.item.memberNote === 'string' && props.item.memberNote.trim() !== '',
);

// The whole collection day is closed — answering is no longer possible, so the
// reserve/OOS buttons are hidden rather than shown disabled (distinct from a
// mid-collection per-item freeze, item_not_pending, where they stay visible).
const isBoxClosed = computed(() => props.item.frozenReason === SupplierFrozenReason.BOX_CLOSED);

// 'own_channel' stays the unmarked default (see template comment) — only
// 'claimed' (pulled from another channel's post, the سحب mechanic) renders.
const isClaimedOrigin = computed(() => props.item.originBadge === 'claimed');

// "A source post exists" — driven by the post body or its own gallery
// (`catalogPhotos`, distinct from the single product `photoUrl`), matching what
// the sheet (Section 3.2) actually has to show.
const hasSourcePost = computed(() => {
  const hasText =
    typeof props.item.postContent === 'string' && props.item.postContent.trim() !== '';
  const hasGallery = Array.isArray(props.item.catalogPhotos) && props.item.catalogPhotos.length > 0;
  return hasText || hasGallery;
});

// Mirrors the model's own lineAmount getter so the fallback (bare fixtures)
// agrees with the real model's computation.
const lineAmount = computed(() => {
  if (typeof props.item.lineAmount === 'number') return props.item.lineAmount;
  return (Number(props.item.qty) || 0) * (Number(props.item.unitPrice) || 0);
});

// RESERVED and PARTIALLY_RESERVED are one visual family (v1 never sends a
// partial reserve, but a server-returned one must still render sensibly).
const isReserveActive = computed(() => isReservedSignal(props.item.signal));
const isOosActive = computed(() => isOosSignal(props.item.signal));

// De-emphasized (not disabled) when the OTHER button is the active answer —
// still one tap away from switching directly, per the toggle mechanics.
const reserveVariant = computed(() => {
  if (isReserveActive.value) return 'active';
  if (isOosActive.value) return 'muted';
  return 'idle';
});
const oosVariant = computed(() => {
  if (isOosActive.value) return 'active';
  if (isReserveActive.value) return 'muted';
  return 'idle';
});
const reserveBtnClass = computed(
  () => `daily-order-item-card__btn--reserve-${reserveVariant.value}`,
);
const oosBtnClass = computed(() => `daily-order-item-card__btn--oos-${oosVariant.value}`);

const reserveLabel = computed(() =>
  isReserveActive.value
    ? t('supplier.dailyOrders.actions.reserveActive')
    : t('supplier.dailyOrders.actions.reserveIdle'),
);
const reserveAriaLabel = computed(() =>
  t('supplier.dailyOrders.actions.reserveAriaWithItem', { item: itemName.value }),
);
const oosAriaLabel = computed(() =>
  t('supplier.dailyOrders.actions.oosAriaWithItem', { item: itemName.value }),
);

// f7-button's `disabled` prop is CSS-class-only (confirmed against
// framework7-vue/components/button.js — it never becomes a native DOM
// attribute regardless of tag) and a "disabled" f7-button still fires clicks.
// This guard is the actual enforcement, not belt-and-braces.
function onReserveClick() {
  if (props.item.frozen) return;
  if (isReserveActive.value) {
    emit('clear', props.item.id);
  } else {
    emit('reserve', props.item.id);
  }
}
function onOosClick() {
  if (props.item.frozen) return;
  if (isOosActive.value) {
    emit('clear', props.item.id);
  } else {
    emit('oos', props.item.id);
  }
}

function onLockTap() {
  showToast(t(frozenReasonI18nKey(props.item.frozenReason)));
}
</script>

<style scoped>
.daily-order-item-card {
  margin: 12px 16px;
  border-radius: var(--poolit-radius-lg, 16px);
  overflow: hidden;
}

.daily-order-item-card__main {
  display: flex;
  gap: var(--poolit-spacing-md, 16px);
  padding: var(--poolit-spacing-md, 16px);
}

.daily-order-item-card__media {
  position: relative;
  flex-shrink: 0;
}

.daily-order-item-card__photo {
  width: 72px;
  height: 72px;
  border-radius: var(--poolit-radius-md, 8px);
  object-fit: cover;
  background: var(--f7-color-gray-100, #f1f3f5);
}

.daily-order-item-card__photo--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--poolit-text-muted, #6b7280);
}

.daily-order-item-card__photo-badge {
  position: absolute;
  inset-block-end: 4px;
  inset-inline-end: 4px;
  padding: 1px 6px;
  border-radius: var(--poolit-radius-full, 9999px);
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  font-size: 11px;
  font-weight: var(--poolit-font-weight-semibold, 600);
}

.daily-order-item-card__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.daily-order-item-card__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.daily-order-item-card__name {
  font-size: 15px;
  font-weight: var(--poolit-font-weight-semibold, 600);
  color: var(--f7-text-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.daily-order-item-card__lock {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}

.daily-order-item-card__lock-chip {
  --f7-chip-height: 22px;
  --f7-chip-font-size: 12px;
  color: var(--poolit-text-secondary, #374151);
  background: var(--poolit-bg-tertiary, #e2e8f0);
  gap: 4px;
}

.daily-order-item-card__chips {
  display: flex;
  align-self: flex-start;
  flex-wrap: wrap;
  gap: 6px;
}

.daily-order-item-card__variant {
  --f7-chip-height: 22px;
  --f7-chip-font-size: 12px;
  color: var(--poolit-secondary-700, #2766bc);
  background: var(--poolit-secondary-50, #eff7ff);
}

.daily-order-item-card__origin {
  --f7-chip-height: 22px;
  --f7-chip-font-size: 12px;
  color: var(--poolit-text-secondary, #374151);
  background: var(--poolit-bg-tertiary, #e2e8f0);
}

.daily-order-item-card__amounts {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.daily-order-item-card__qty {
  font-size: 16px;
  font-weight: var(--poolit-font-weight-semibold, 600);
  color: var(--f7-text-color);
}

.daily-order-item-card__unit {
  font-size: 16px;
  font-weight: var(--poolit-font-weight-semibold, 600);
  color: var(--poolit-primary-700, var(--f7-theme-color));
}

.daily-order-item-card__line-total {
  font-size: 13px;
  color: var(--poolit-text-muted, #6b7280);
}

.daily-order-item-card__note {
  margin: 0 16px 12px;
  padding: 8px 12px;
  border-inline-start: 3px solid var(--poolit-warning-500, #f59e0b);
  background: var(--poolit-warning-50, #fffbeb);
  border-radius: var(--poolit-radius-sm, 4px);
}

/* Empty state — neutral, not warning-tinted, so it reads as "nothing here"
   rather than "something needs your attention". */
.daily-order-item-card__note--empty {
  border-inline-start-color: var(--poolit-border-color, #e2e8f0);
  background: var(--poolit-bg-secondary, #f8fafc);
}

.daily-order-item-card__note-empty {
  margin: 0;
  color: var(--poolit-text-muted, #6b7280);
  font-size: 13px;
}

.daily-order-item-card__note-label {
  display: block;
  margin-bottom: 2px;
  font-size: 12px;
  font-weight: var(--poolit-font-weight-semibold, 600);
  color: var(--poolit-warning-700, #b45309);
}

.daily-order-item-card__note-body {
  margin: 0;
  color: var(--poolit-text-secondary, #374151);
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.daily-order-item-card__view-post {
  display: flex;
  align-items: center;
  min-height: 44px;
  margin: 0 16px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--poolit-text-link, var(--poolit-secondary-500));
  font-size: 14px;
  font-weight: var(--poolit-font-weight-medium, 500);
  text-align: start;
  cursor: pointer;
}

.daily-order-item-card__actions {
  display: flex;
  align-items: stretch;
  gap: var(--poolit-spacing-sm, 8px);
  padding: 12px 16px 16px;
}

.daily-order-item-card__btn {
  flex: 1;
  min-height: 44px;
  gap: 6px;
  transition: background-color var(--poolit-transition-fast, 150ms)
    var(--poolit-transition-ease, ease);
}

/* ── Active-filled: real --poolit-* tokens, not F7's default theme color ──── */
.daily-order-item-card__btn--reserve-active {
  --f7-button-fill-bg-color: var(--poolit-success-700, #047857);
  --f7-button-fill-text-color: #fff;
}

.daily-order-item-card__btn--oos-active {
  --f7-button-fill-bg-color: var(--poolit-error-700, #b91c1c);
  --f7-button-fill-text-color: #fff;
}

/* ── Idle (unanswered) — neutral outline ──────────────────────────────────── */
.daily-order-item-card__btn--reserve-idle,
.daily-order-item-card__btn--oos-idle {
  --f7-button-text-color: var(--poolit-text-secondary, #374151);
  --f7-button-outline-border-color: var(--poolit-border-color, #e2e8f0);
}

/* ── De-emphasized sibling of an active answer — still one tap away ──────── */
.daily-order-item-card__btn--reserve-muted,
.daily-order-item-card__btn--oos-muted {
  --f7-button-text-color: var(--poolit-text-muted, #6b7280);
  --f7-button-outline-border-color: var(--poolit-border-color, #e2e8f0);
}

.dark .daily-order-item-card__photo {
  background: var(--f7-color-gray-800, #1f2937);
}

@media (prefers-reduced-motion: reduce) {
  .daily-order-item-card__btn {
    transition: none;
  }
}
</style>
